/**
 * Contratos de la biblioteca en el Studio: la firma mínima (ADR-14).
 *
 * Qué viaja por aquí:
 * - La biblioteca tal cual está en git (`KnowledgeItem`): cuadernos,
 *   fichas, su cuerpo markdown, sus preguntas `[CRITERIO PENDIENTE]` y el
 *   hash del cuerpo (la función de `check-fichas --hash`). La API la lee
 *   del disco del despliegue; el Studio nunca la edita (INV-KI-2).
 * - Las firmas de los socios (`PartnerSignoff`): inmutables, con los dos
 *   hashes; si están vigentes o caducadas se CALCULA en cada lectura
 *   (INV-PS-2), nunca se guarda.
 * - El dueño de cada cuaderno (`NotebookOwnership`).
 * - Dos rutas de servicio para el volcado a git (`pendientes`, `volcado`):
 *   no llevan bearer de Entra sino la cabecera `X-Library-Dump-Key`.
 *
 * Las claves van en español, como el resto del Studio; los valores que el
 * contrato de ficha ya fija en inglés (`kind`, `status`) viajan tal cual.
 */
import { z } from "zod";

// ── Vocabulario ───────────────────────────────────────────────────────

export const SIGNOFF_KINDS = ["criterio", "revision", "despacho"] as const;
export const SIGNOFF_DECISIONS = ["vale", "asi-no"] as const;
export const ITEM_STATUSES = ["draft", "reviewed-ai", "house", "reviewed", "published", "retired", "blocked"] as const;
export const ITEM_KINDS = ["definition", "indicator", "pattern", "fact", "sector"] as const;

/**
 * Roles de aplicación de Entra (claim `roles` del token de `appreg-skein-api`).
 * - `Library.Sign`: el rol de biblioteca — ve la biblioteca y firma; no
 *   llega al inspector de las casas de prueba (ADR-14 pt 6, ADR-17).
 * - `Studio.Curator` / `Studio.Admin`: el acceso completo del Skein Team;
 *   también firman, y además reasignan dueños, registran `despacho` y
 *   resuelven «así no».
 */
export const LIBRARY_ROLES = { sign: "Library.Sign", curator: "Studio.Curator", admin: "Studio.Admin" } as const;

/** La respuesta que deja el marcador en su sitio (ADR-14 pt 3). Se compara sin mayúsculas, acentos ni puntuación final. */
export const NO_LO_SE = "no lo sé";

/** Cabecera de las rutas de servicio del volcado (sin Entra; servicio a servicio). */
export const LIBRARY_DUMP_HEADER = "x-library-dump-key";

/** Topes de lo que escribe un socio: una respuesta es un párrafo, no un documento. */
export const ANSWER_MAX = 2000;
/** Ninguna ficha tiene más de unas pocas preguntas; el tope solo corta abusos. */
export const ANSWERS_MAX = 50;
export const COMMENT_MAX = 4000;
export const REFERENCE_MAX = 500;

const SignoffKind = z.enum(SIGNOFF_KINDS);
const SignoffDecision = z.enum(SIGNOFF_DECISIONS);
const ItemStatus = z.enum(ITEM_STATUSES);
const Hash = z.string().regex(/^[0-9a-f]{64}$/, "sha256 en hexadecimal");
const IsoDateTime = z.string().datetime();
const NotebookCode = z.string().regex(/^C\d{2}$/, "código de cuaderno Cnn");

export const Persona = z.object({ oid: z.string(), name: z.string() });

/** Cuántas fichas hay en cada estado de git. */
export const EstadosFichas = z.object({
  draft: z.number().int().nonnegative(),
  reviewedAi: z.number().int().nonnegative(),
  house: z.number().int().nonnegative(),
  reviewed: z.number().int().nonnegative(),
  published: z.number().int().nonnegative(),
  retired: z.number().int().nonnegative(),
  blocked: z.number().int().nonnegative(),
});

// ── La biblioteca ─────────────────────────────────────────────────────

export const CuadernoResumen = z.object({
  code: NotebookCode,
  title: z.string(),
  /** Área A–J de `content/library/cuadernos.json`; «?» mientras ese fichero no exista. */
  area: z.string(),
  areaName: z.string(),
  /** El cuaderno entero pide el visto del despacho (C07, C16, C21, C22). */
  professionalReview: z.boolean(),
  fichas: z.number().int().nonnegative(),
  /** Preguntas de fichas en `reviewed-ai` que aún no tienen un `criterio` «vale» vigente. */
  preguntasPendientes: z.number().int().nonnegative(),
  /** Todos los marcadores `[CRITERIO PENDIENTE]` del cuaderno, en cualquier estado y firmados o no. */
  preguntasTotal: z.number().int().nonnegative(),
  /** Fichas con un `criterio` «vale» vigente (volcado o no) o ya en `house` o más allá. */
  firmadas: z.number().int().nonnegative(),
  dueno: Persona.nullable(),
  /** La firma más reciente del cuaderno (ISO), o null. */
  ultimaFirma: IsoDateTime.nullable(),
  estados: EstadosFichas,
});

export const BibliotecaMe = z.object({
  oid: z.string(),
  name: z.string(),
  /** `completo` = Studio.Curator o Studio.Admin; `biblioteca` = solo Library.Sign. */
  rol: z.enum(["biblioteca", "completo"]),
  /** Códigos de los cuadernos de los que soy dueño. */
  misCuadernos: z.array(NotebookCode),
});

export const Biblioteca = z.object({
  cuadernos: z.array(CuadernoResumen),
  me: BibliotecaMe,
  /** La suma de `preguntasTotal` de todos los cuadernos. */
  preguntasTotal: z.number().int().nonnegative(),
});

// ── Firmas ────────────────────────────────────────────────────────────

/**
 * - `vigente`: el cuerpo actual tiene su `baseHash` o su `resultHash` y es la última de su clase con esa condición.
 * - `sustituida`: también casa con el cuerpo, pero hay otra posterior de su clase.
 * - `caducada`: el cuerpo cambió por el método (INV-PS-2).
 */
export const FIRMA_ESTADOS = ["vigente", "sustituida", "caducada"] as const;
/**
 * - `volcada`: el fichero de git (el del despliegue) ya la lleva.
 * - `en-pr`: está en un PR del volcado que aún no se ve en git.
 * - `pendiente`: aún no se ha volcado.
 * - `no-aplica`: un «así no» de `criterio` o `despacho`, que nunca toca el fichero (va a la cola de revisión).
 */
export const FIRMA_VOLCADOS = ["pendiente", "en-pr", "volcada", "no-aplica"] as const;

export const Resolucion = z.object({ id: z.string(), by: Persona, note: z.string(), createdAt: IsoDateTime });

export const Firma = z.object({
  id: z.string(),
  kind: SignoffKind,
  signer: Persona,
  baseHash: Hash,
  resultHash: Hash,
  decision: SignoffDecision,
  answers: z.array(z.string()),
  comment: z.string().nullable(),
  reference: z.string().nullable(),
  createdAt: IsoDateTime,
  estado: z.enum(FIRMA_ESTADOS),
  volcado: z.enum(FIRMA_VOLCADOS),
  /** El PR del volcado que la lleva, si lo hay. */
  prUrl: z.string().nullable(),
  /** Solo en un «así no»: el cierre del Skein Team, si lo hay. */
  resolucion: Resolucion.nullable(),
});

export const FirmaResumen = Firma.pick({ id: true, decision: true, signer: true, createdAt: true, volcado: true });
export const FirmasPorClase = z.object({ criterio: FirmaResumen.nullable(), revision: FirmaResumen.nullable(), despacho: FirmaResumen.nullable() });

export const FichaResumen = z.object({
  slug: z.string(),
  title: z.string(),
  kind: z.string(),
  status: ItemStatus,
  /** Número de preguntas `[CRITERIO PENDIENTE]` en el cuerpo actual. */
  preguntas: z.number().int().nonnegative(),
  professionalReview: z.boolean(),
  firmaVigente: FirmasPorClase,
  /** Clases con un «así no» vigente y sin resolver (INV-PS-4). */
  bloqueos: z.array(SignoffKind),
});

export const CuadernoFichas = z.object({ cuaderno: CuadernoResumen, fichas: z.array(FichaResumen) });

// ── La ficha ──────────────────────────────────────────────────────────

export const Pregunta = z.object({ seccion: z.string(), texto: z.string() });

export const FuenteFicha = z.object({
  id: z.string(),
  title: z.string(),
  author: z.string(),
  year: z.string(),
  url: z.string().nullable(),
  license: z.string(),
  /** `ai_optout`: la fuente reserva su contenido frente al uso por IA. */
  aiOptout: z.boolean(),
});

/** Una entrada de `review_log` (la ronda de críticos y juez). */
export const EntradaRevision = z.object({
  /** La vuelta; null si el frontmatter no la trae como número. */
  round: z.number().int().nullable(),
  critic: z.string(),
  verdict: z.string(),
  notes: z.string(),
  /** Solo en algunas entradas (p. ej. el visto del despacho): el hash del cuerpo que vieron. */
  hash: z.string().nullable(),
});

/** Una ficha de `related_items` resuelta en el catálogo. */
export const FichaRelacionada = z.object({ slug: z.string(), title: z.string(), notebook: NotebookCode });

export const PuedoFirmar = z.object({
  criterio: z.boolean(),
  revision: z.boolean(),
  despacho: z.boolean(),
  /** Por qué NO, por clase (solo las clases que no puedo firmar). */
  porque: z.record(SignoffKind, z.string()),
});

export const Ficha = z.object({
  slug: z.string(),
  notebook: NotebookCode,
  title: z.string(),
  kind: z.string(),
  layer: z.string(),
  status: ItemStatus,
  summary: z.string(),
  curator: z.string(),
  date: z.string(),
  validUntil: z.string().nullable(),
  houseCriteriaBy: z.string().nullable(),
  reviewedBy: z.string().nullable(),
  professionalReview: z.boolean(),
  /** `intake` del frontmatter (`ai-assisted`, `manual`…); «» si falta. */
  intake: z.string(),
  sources: z.array(FuenteFicha),
  /** Los slugs de `related_items` tal cual. */
  relatedItems: z.array(z.string()),
  /** `related_items` resueltos en el catálogo, en su orden; los que no existen se omiten. */
  relacionadas: z.array(FichaRelacionada),
  /** Los slugs de `related_skills` tal cual. */
  relatedSkills: z.array(z.string()),
  /** El `review_log` del frontmatter, en su orden. */
  reviewLog: z.array(EntradaRevision),
  /** El cuerpo markdown tal cual (sin frontmatter). */
  body: z.string(),
  /** Las preguntas en el orden del cuerpo: `answers[i]` contesta `preguntas[i]`. */
  preguntas: z.array(Pregunta),
  /** Hash del cuerpo actual: el `baseHash` que hay que mandar al firmar. */
  hash: Hash,
  dueno: Persona.nullable(),
  /** Todas, de la más antigua a la más reciente. */
  firmas: z.array(Firma),
  bloqueos: z.array(SignoffKind),
  puedoFirmar: PuedoFirmar,
});

// ── Peticiones ────────────────────────────────────────────────────────

const Answer = z.string().trim().min(1, "respuesta vacía").max(ANSWER_MAX);

export const VistaPreviaRequest = z.object({ answers: z.array(Answer).max(ANSWERS_MAX) }).strict();
export const VistaPrevia = z.object({
  /** El cuerpo compuesto con las respuestas (lo que firmaría). */
  body: z.string(),
  resultHash: Hash,
  /** El cuerpo compuesto cumple el contrato de ficha (las reglas de cuerpo de `check-fichas`). */
  valida: z.boolean(),
  problemas: z.array(z.string()),
});

export const FirmaRequest = z.object({
  kind: SignoffKind,
  decision: SignoffDecision,
  /** Solo `criterio`: una por pregunta, en orden; «no lo sé» vale. */
  answers: z.array(Answer).max(ANSWERS_MAX).optional(),
  /** Obligatorio en un «así no». */
  comment: z.string().trim().max(COMMENT_MAX).optional(),
  /** Obligatoria en `despacho`: la referencia del visto del despacho. */
  reference: z.string().trim().max(REFERENCE_MAX).optional(),
  /** El hash del cuerpo que el socio leyó (`Ficha.hash`). */
  baseHash: Hash,
  /**
   * El hash de lo que firma: en `criterio` «vale», el `resultHash` de la
   * vista previa; en el resto, el mismo `baseHash`. Si el servidor compone
   * otra cosa, 409 `stale` (hay que volver a mirar la vista previa).
   */
  resultHash: Hash,
}).strict();

export const DuenoRequest = z.object({
  accion: z.enum(["tomar", "soltar", "reasignar"]),
  /** Solo `reasignar`. */
  ownerOid: z.string().min(1).optional(),
  ownerName: z.string().min(1).optional(),
}).strict();

export const ResolucionRequest = z.object({ note: z.string().trim().min(1).max(COMMENT_MAX) }).strict();

// ── Volcado (servicio a servicio) ─────────────────────────────────────

export const FirmaPendiente = Firma.omit({ estado: true }).extend({
  notebook: NotebookCode,
  slug: z.string(),
  /** Hash del cuerpo de la ficha en el despliegue de la API. */
  fichaHash: Hash,
  /** Estado de git de la ficha en el despliegue de la API. */
  fichaStatus: ItemStatus,
  /**
   * Solo `criterio` «vale»: el cuerpo compuesto, cuyo sha256 es `resultHash`.
   * El volcado lo escribe tal cual (tras comprobar que el fichero sigue en `baseHash`).
   */
  resultBody: z.string().nullable(),
  /** Quedan marcadores («no lo sé»): se vuelca el cuerpo pero la ficha sigue en `reviewed-ai`. */
  quedanPreguntas: z.boolean(),
  /** `revision` «vale» en una ficha con `professional_review` sin `despacho` vigente: espera. */
  esperaDespacho: z.boolean(),
});
/** El dueño vigente de cada cuaderno: el volcado regenera con esto la fila «Dueño» de las specs. */
export const DuenoCuaderno = z.object({ notebook: NotebookCode, owner: Persona });
export const FirmasPendientes = z.object({ firmas: z.array(FirmaPendiente), duenos: z.array(DuenoCuaderno) });

/** El PR del volcado: solo GitHub. */
export const PR_URL = /^https:\/\/github\.com\/[^\s]+$/;
export const VolcadoRequest = z.object({ signoffIds: z.array(z.string().min(1)).min(1).max(1000), prUrl: z.string().regex(PR_URL, "URL de un PR de GitHub") }).strict();
export const Volcado = z.object({ id: z.string(), prUrl: z.string(), signoffIds: z.array(z.string()), createdAt: IsoDateTime });

// ── Rutas ─────────────────────────────────────────────────────────────

export const LIBRARY_ROUTES = {
  biblioteca: "/studio/biblioteca",
  cuaderno: (code: string) => `/studio/biblioteca/cuadernos/${encodeURIComponent(code)}`,
  cuadernoPattern: "/studio/biblioteca/cuadernos/:cuaderno",
  dueno: (code: string) => `/studio/biblioteca/cuadernos/${encodeURIComponent(code)}/dueno`,
  duenoPattern: "/studio/biblioteca/cuadernos/:cuaderno/dueno",
  ficha: (slug: string) => `/studio/biblioteca/fichas/${encodeURIComponent(slug)}`,
  fichaPattern: "/studio/biblioteca/fichas/:slug",
  vistaPrevia: (slug: string) => `/studio/biblioteca/fichas/${encodeURIComponent(slug)}/vista-previa`,
  vistaPreviaPattern: "/studio/biblioteca/fichas/:slug/vista-previa",
  firmas: (slug: string) => `/studio/biblioteca/fichas/${encodeURIComponent(slug)}/firmas`,
  firmasPattern: "/studio/biblioteca/fichas/:slug/firmas",
  resolucion: (signoffId: string) => `/studio/biblioteca/firmas/${encodeURIComponent(signoffId)}/resolucion`,
  resolucionPattern: "/studio/biblioteca/firmas/:id/resolucion",
  /** Contadores de uso sin casa (ADR-20 pt 4). */
  telemetria: "/studio/biblioteca/telemetria",
  /** Servicio a servicio, con `X-Library-Dump-Key`. */
  pendientes: "/studio/biblioteca/firmas/pendientes",
  volcado: "/studio/biblioteca/firmas/volcado",
} as const;

export type SignoffKind = z.infer<typeof SignoffKind>;
export type SignoffDecision = z.infer<typeof SignoffDecision>;
export type ItemStatus = z.infer<typeof ItemStatus>;
export type Persona = z.infer<typeof Persona>;
export type EstadosFichas = z.infer<typeof EstadosFichas>;
export type CuadernoResumen = z.infer<typeof CuadernoResumen>;
export type BibliotecaMe = z.infer<typeof BibliotecaMe>;
export type Biblioteca = z.infer<typeof Biblioteca>;

/**
 * `GET /studio/biblioteca/telemetria` (ADR-20 pt 4; R-5 anotada): los
 * contadores sin casa que el Advisor incrementa al escribir cada traza.
 * Sin Engagement, sin texto, sin identidad. `porFichaVisible` es false
 * (y `porSkill` y `masCitadas` van vacíos) en producción mientras el
 * despliegue tenga menos de cinco casas: con una o dos, identificarían al
 * cliente. En `pre`, siempre visible.
 */
export const BibliotecaTelemetria = z.object({
  turnos: z.number().int(),
  /** Turnos sin ninguna ficha citada. */
  sinFicha: z.number().int(),
  /** Del enrutado: el método elegido no era el primer candidato; el modelo dijo «ninguno». */
  enrutado: z.object({ distintoDelPrimero: z.number().int(), ninguno: z.number().int() }),
  porSkill: z.array(z.object({ slug: z.string(), title: z.string().nullable(), turnos: z.number().int(), sinFicha: z.number().int() })),
  masCitadas: z.array(z.object({ slug: z.string(), title: z.string().nullable(), notebook: z.string().nullable(), veces: z.number().int() })),
  porFichaVisible: z.boolean(),
});
export type BibliotecaTelemetria = z.infer<typeof BibliotecaTelemetria>;
export type Resolucion = z.infer<typeof Resolucion>;
export type Firma = z.infer<typeof Firma>;
export type FirmaResumen = z.infer<typeof FirmaResumen>;
export type FirmasPorClase = z.infer<typeof FirmasPorClase>;
export type FichaResumen = z.infer<typeof FichaResumen>;
export type CuadernoFichas = z.infer<typeof CuadernoFichas>;
export type Pregunta = z.infer<typeof Pregunta>;
export type FuenteFicha = z.infer<typeof FuenteFicha>;
export type EntradaRevision = z.infer<typeof EntradaRevision>;
export type FichaRelacionada = z.infer<typeof FichaRelacionada>;
export type PuedoFirmar = z.infer<typeof PuedoFirmar>;
export type Ficha = z.infer<typeof Ficha>;
export type VistaPreviaRequest = z.infer<typeof VistaPreviaRequest>;
export type VistaPrevia = z.infer<typeof VistaPrevia>;
export type FirmaRequest = z.infer<typeof FirmaRequest>;
export type DuenoRequest = z.infer<typeof DuenoRequest>;
export type ResolucionRequest = z.infer<typeof ResolucionRequest>;
export type FirmaPendiente = z.infer<typeof FirmaPendiente>;
export type DuenoCuaderno = z.infer<typeof DuenoCuaderno>;
export type FirmasPendientes = z.infer<typeof FirmasPendientes>;
export type VolcadoRequest = z.infer<typeof VolcadoRequest>;
export type Volcado = z.infer<typeof Volcado>;
